<template>
  <div title="Choice interaction" class="question-node" :class="{ selected: isSelected, collapsed: isCollapsed }">
    <!-- En-tête -->
    <div class="node-header">
      <button @click="toggleCollapse" class="collapse-btn" title="Réduire / développer">
        <span class="caret" :class="{ rotated: isCollapsed }">▲</span>
      </button>

      <p class="question-text">


        <span class="textQuestioHeader"> {{ truncatedText || 'Question sans texte' }}</span>
      </p>

      <div class="d-flex justify-content-between align-items-center">
        <button hidden class="btn btn-sm btn-outline-danger" @click.stop.prevent="onDelete">✕</button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="node-toolbar">
      <button class="toolbar-btn edit" @click="onOpenEditor" title="Éditer le contenu">
        <i class="fas fa-pen"></i>
      </button>

      <button class="toolbar-btn mode" :class="localMode === 'options' ? 'optionMode' : 'scoreMode'"
        @click.stop="toggleMode"
        :title="localMode === 'options' ? 'Mode Options (cliquez pour basculer en Score)' : 'Mode Score (cliquez pour basculer en Options)'">
        <i :class="localMode === 'options' ? 'fas fa-list-ol' : 'fas fa-calculator'"></i>
      </button>

      <button class="toolbar-btn delete" @click.stop="onDelete" title="Supprimer le nœud">
        <i class="fas fa-trash"></i>
      </button>
    </div>

    <!-- Contenu du nœud avec animation -->
    <transition name="fade-slide">
      <div v-if="!isCollapsed" class="node-content">
        <div class="options">
          <div v-for="(opt, index) in element?.options || []" :key="opt.id" class="option">
            <span class="option-letter">{{ String.fromCharCode(65 + index) }}</span>
            <span class="option-text">{{ opt.text }}</span>
          </div>
        </div>
      </div>
    </transition>

<!-- Handles de connexion (cachés si nested node) -->
<div v-if="!parentNode" class="connection-handles">
  <!-- Mode options : handlers multiples -->
  <template v-if="localMode === 'options'">
  <Handle
    v-for="(option, index) in element?.options || []"
    :key="`source-${option.id}`"
    :id="option.id"
    type="source"
    :position="Position.Right"
    :style="{
      top: isCollapsed
        ? getCollapsedHandlePosition(option.id)
        : getOptionHandlePosition(option.id)
    }"
    class="option-handle"
    :class="{ hidden: isCollapsed }"
    :is-valid-connection="isValidOutgoingConnection"
  >
    <span class="handle-label">{{ String.fromCharCode(65 + index) }}</span>
  </Handle>
</template>

<!-- Mode score : un seul handler vert -->
<template v-else>
  <Handle
    id="score"
    type="source"
    :position="Position.Right"
    class="score-handle"
    :style="{ top: '50%' }"
    :is-valid-connection="isValidOutgoingConnection"
  >
    <span class="handle-label">S</span>
  </Handle>
</template>
</div>



    <!-- Handle d'entrée (caché si nested node) -->
    <Handle
  v-if="!parentNode"
  id="input"
  type="target"
  :position="Position.Left"
  class="input-handle"
  :is-valid-connection="isValidConnection"
/>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from 'vue'
import { Handle, Position } from '@vue-flow/core'
import { useTestStore } from '../../../stores/testStore'
import { useVueFlow } from '@vue-flow/core'


// Récupère l’instance du flow (accessible depuis tout composant enfant)
const { edges, setEdges, nodes } = useVueFlow()

const props = defineProps({
  id: String,
  data: { type: Object, default: () => ({}) },
  selected: Boolean,
  element: { type: Object, default: null },
  parentNode: { type: String, default: null } // Pour identifier les nested nodes
})

const emit = defineEmits(['delete', 'update', 'open-editor'])
const testStore = useTestStore()
const testData = testStore.testData

// ✅ Initialisation correcte du mode depuis props.data
const localMode = ref(props.data?.mode || 'options')

// --- Gestion du collapse (local + synchro)
const isCollapsed = ref(props.data?.collapsed ?? false)

watch(() => props.data?.collapsed, (val) => {
  isCollapsed.value = !!val
})

// 🔁 Synchronisation bidirectionnelle si le flow recharge les nodes
watch(
  () => props.data?.mode,
  (val) => {
    if (val && val !== localMode.value) {
      console.log(`🔄 Synchro mode externe: ${val} pour node ${props.id}`)
      localMode.value = val
    }
  },
  { immediate: true }
)

const toggleCollapse = async (event) => {
  event.stopPropagation()
  event.preventDefault()
  isCollapsed.value = !isCollapsed.value
  emit('update', { id: props.id, data: { collapsed: isCollapsed.value } })
  await nextTick()
}

const truncatedText = computed(() => {
  const text = props.element?.el_Text || 'Question sans texte'
  if (!text) return ''
  return text.length > 30 ? text.slice(0, 30) + '…' : text
})

// --- Actions
function onDelete() {
  if (window.confirm('⚠️ Are you sure you want to delete this node? \nThis action cannot be undone.')) {
    emit('delete', { nodeId: props.id, elementId: props.data?.elementId })
  }
}

function onOpenEditor() {
  console.log('🔍 [ChoiceNode.onOpenEditor] DÉBUT')
  console.log('  📍 Node ID:', props.id)
  console.log('  📍 Parent Node:', props.parentNode || '(none - standalone node)')
  console.log('  📍 Element ID from props.data:', props.data?.elementId)
  console.log('  📍 Element object:', props.element)
  console.log('  📍 Full props.data:', props.data)

  emit('open-editor', { nodeId: props.id, elementId: props.data?.elementId })

  console.log('🔍 [ChoiceNode.onOpenEditor] FIN - Event émis')
}

// --- Sélection
const isSelected = computed(() => props.selected === true)

// --- Options visibles
const visibleOptions = computed(() => {
  const options = props.element?.options || []
  if (!isCollapsed.value) return options
  const connectedOptions = (testStore.connections || [])
    .filter(conn => conn?.source === props.id)
    .map(conn => conn?.sourceHandle)
    .filter(Boolean)
  return options.filter(option => connectedOptions.includes(option.id))
})

// --- Position des handles
const getOptionHandlePosition = (optionId) => {
  const options = props.element?.options || []
  const optionIndex = options.findIndex(opt => opt.id === optionId)
  if (optionIndex === -1) return '60px'
  const baseOffset = 60
  const spacing = 25
  return `${baseOffset + optionIndex * spacing}px`
}

const getCollapsedHandlePosition = (optionId) => {
  const list = visibleOptions.value || []
  if (!Array.isArray(list) || list.length === 0) return '0px'
  const visibleIndex = list.findIndex(opt => opt.id === optionId)
  const baseOffset = 25
  const spacing = 15
  return `${baseOffset + ((visibleIndex >= 0 ? visibleIndex : list.length) * spacing)}px`
}

// Bascule du mode
async function toggleMode() {
  const allEdges = edges.value || []
  const currentMode = localMode.value
  const newMode = currentMode === 'options' ? 'score' : 'options'

  // Connexions liées à ce node
  const relatedConnections = allEdges.filter(
    conn => conn.source === props.id
  )

  if (relatedConnections.length > 0) {
    const message =
      currentMode === 'options'
        ? `⚠️ Passer en mode "Score" supprimera ${relatedConnections.length} connexion(s) existante(s).\nSouhaitez-vous continuer ?`
        : `⚠️ Passer en mode "Options" supprimera la connexion actuelle du score.\nSouhaitez-vous continuer ?`

    if (!window.confirm(message)) {
      return
    }

    // ✅ Ne supprimer QUE les connexions sortantes de ce node
    const filtered = allEdges.filter(conn => conn.source !== props.id)
    setEdges(filtered)
  }

  // ✅ Mise à jour réactive du mode local
  localMode.value = newMode

  // ✅ Mise à jour de props.data (pour réactivité Vue Flow)
  if (props.data) {
    props.data.mode = newMode
  }

  // 🔄 Émet un update pour le parent ConditionalTestEditor qui se chargera de la sauvegarde
  emit('update', { id: props.id, data: { ...props.data, mode: newMode } })

  // ✅ Attendre un cycle pour forcer le rafraîchissement
  await nextTick()

  console.log(`✅ Mode basculé vers "${newMode}" pour node ${props.id}`)
}



function isValidConnection(connection) {
  console.log('🎯 Tentative de connexion détectée:', connection)
  const allNodes = nodes.value || []
  const source = allNodes.find(n => n.id === connection.source)
  const sourceMode = source?.data?.mode
  console.log('Source node mode:', sourceMode)
  // Autoriser uniquement les nodes verts (score)
  return sourceMode === 'score'
}

function isValidOutgoingConnection(connection) {
  const allNodes = nodes.value || []
  const target = allNodes.find(n => n.id === connection.target)
  const targetType = target?.type

  // 🟢 Mode SCORE : peut UNIQUEMENT se connecter à un switcher
  if (localMode.value === 'score') {
    if (targetType !== 'switcher') {
      console.log('🚫 Connexion interdite : mode "score" ne peut se connecter qu\'à un switcher')
      return false
    }
    console.log('✅ Connexion autorisée : score → switcher')
    return true
  }

  // 🔵 Mode OPTIONS : peut se connecter à tout SAUF switcher
  if (localMode.value === 'options') {
    if (targetType === 'switcher') {
      console.log('🚫 Connexion interdite : mode "options" ne peut pas se connecter à un switcher')
      return false
    }
    console.log('✅ Connexion autorisée : options → autre node')
    return true
  }

  return true
}




</script>

<style scoped>
.question-node {
  background: white;
  border: 2px solid #ddd;
  border-radius: 8px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.3s ease;
}

.question-node.collapsed {
  min-width: 220px;
  max-width: 260px;
}

.question-node.selected {
  border-color: #0d6efd;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  transform: translateY(-2px);
}

/* Header */
.node-header {
  background-color: #f8f9fa;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapse-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  padding: 4px 8px;
  border-radius: 3px;
  transition: all 0.2s ease;
}

.collapse-btn:hover {
  color: #0d6efd;
}

.caret {
  display: inline-block;
  transition: transform 0.25s ease;
  font-size: 12px;
}

.caret.rotated {
  transform: rotate(180deg);
}

/* Toolbar */
.node-toolbar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 20px;
}

.toolbar-btn {
  border: none;
  background: #f8f9fa;
  color: #555;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.toolbar-btn:hover {
  background: #e9ecef;
  color: #0d6efd;
  transform: translateY(-1px);
}

.toolbar-btn:active {
  transform: scale(0.95);
}

/* Options internes */
.node-content {
  padding: 15px;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option {
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #ddd;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.option:hover {
  transform: translateX(4px);
  background-color: #eef4ff;
}

.option-letter {
  font-weight: bold;
  color: #007bff;
  margin-right: 10px;
  min-width: 20px;
}

/* Handles */
.connection-handles {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
}

.option-handle {
  width: 22px !important;
  height: 22px !important;
  background-color: #0d6efd !important;
  border: 2px solid white !important;
  color: white;
  border-radius: 6px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transform: translateX(50%);
  cursor: crosshair;
  position: relative;
  opacity: 1;
  transition: opacity 0.25s ease, transform 0.25s ease, background-color 0.2s ease;
}

.option-handle.hidden {
  opacity: 0;
  transform: translateX(50%) scale(0.6);
  pointer-events: none;
}

.option-handle:hover {
  background-color: #0b5ed7 !important;
  transform: translateX(50%) scale(1.1);
}

.question-node.collapsed .option-handle {
  opacity: 0.5;
  transform: translateX(50%) scale(0.85);
}

/* Transition contenu */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.question-text {
  position: absolute;
  margin-left: 30px;
  margin-top: 21px;
}

.node-icon {
  position: absolute;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: #007bff;
  /* bleu vif */
  color: white;
  font-weight: bold;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.node-icon:hover {
  transform: scale(1.1);
  background-color: #0056b3;
}

.textQuestioHeader {
  margin-left: 0px;
  color: #0056b3;
}

.toolbar-btn.optionMode {
  color: #0B5ED7 !important;
  background-color: #e9f2ff;
  /* facultatif : fond bleu clair pour mieux voir */
}

.toolbar-btn.scoreMode {
  color: #83e76a !important;
  background-color: #f3fff3;
  /* facultatif : fond vert clair */
}

.mode {
  transition: all 0.3s ease;
}

.score-handle {
  width: 24px !important;
  height: 24px !important;
  background-color: #28a745 !important; /* vert */
  border: 2px solid white !important;
  border-radius: 6px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  transform: translateX(50%);
  cursor: crosshair;
  color: white;
  font-weight: bold;
  transition: all 0.25s ease;
}
.score-handle:hover {
  background-color: #218838 !important;
  transform: translateX(50%) scale(1.1);
}

.vue-flow__handle {
    width: 16px;
    height: 16px;
    background-color: #6c757d ;
    border: 1px solid #fff;
    border-radius: 100%;

}

</style>
